import React, { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** V4.9.8 — optional group label. Options that share a group name are
   *  rendered together under a section header. Options without a group
   *  are rendered flat (same as before). */
  group?: string;
}

interface Props {
  label: string;
  options: ReadonlyArray<MultiSelectOption>;
  value: string[];
  onChange: (next: string[]) => void;
  /** Placeholder text shown when nothing is selected. Default: "All". */
  emptyLabel?: string;
  className?: string;
}

/**
 * Replacement for the ugly multi-select scroll boxes. Click the trigger to
 * open a dropdown of checkboxes. Includes a top "All" row that toggles every
 * option on/off in one click. Clicking an already-selected item deselects it
 * (true toggle, fixes the bug where selections were sticky).
 *
 * Selection summary on the trigger reads:
 *   - "All"                           when nothing selected (or all selected)
 *   - "Open, Resolved"                when ≤2 items selected
 *   - "3 selected"                    when 3+ items selected
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  emptyLabel = 'All',
  className
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const allSelected = value.length === options.length && options.length > 0;
  const noneSelected = value.length === 0;

  const toggle = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  const summary = (() => {
    if (noneSelected || allSelected) return emptyLabel;
    if (value.length <= 2) {
      return value
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }
    return `${value.length} selected`;
  })();

  return (
    <div
      ref={ref}
      className={`form-group multi-select ${open ? 'is-open' : ''} ${className ?? ''}`}
      style={{ margin: 0 }}
    >
      <label className="form-label">{label}</label>
      <button
        type="button"
        className="form-select multi-select-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="multi-select-summary">{summary}</span>
        <span className="multi-select-caret" aria-hidden>▾</span>
      </button>
      {open ? (
        <div className="multi-select-menu" role="listbox">
          <label className="multi-select-option multi-select-all">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
            />
            <span>{allSelected ? 'Clear all' : 'Select all'}</span>
          </label>
          <div className="multi-select-divider" />
          {renderGroupedOptions(options, value, toggle)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * V4.9.8 — Render options either flat (no group field) or grouped with
 * section headers when any option carries a `group` property. Used by
 * the Status filter to render A-flow / B-flow / Both sections clearly.
 */
function renderGroupedOptions(
  options: ReadonlyArray<MultiSelectOption>,
  value: string[],
  toggle: (v: string) => void
) {
  const hasGroups = options.some((o) => !!o.group);
  if (!hasGroups) {
    return options.map((opt) => (
      <label key={opt.value} className="multi-select-option">
        <input
          type="checkbox"
          checked={value.includes(opt.value)}
          onChange={() => toggle(opt.value)}
        />
        <span>{opt.label}</span>
      </label>
    ));
  }

  // Group options by their group label, preserving insertion order.
  const byGroup = new Map<string, MultiSelectOption[]>();
  for (const opt of options) {
    const key = opt.group ?? '';
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key)!.push(opt);
  }

  const nodes: React.ReactNode[] = [];
  let isFirst = true;
  for (const [groupName, groupOptions] of byGroup) {
    if (!isFirst) {
      nodes.push(<div key={`div-${groupName}`} className="multi-select-divider" />);
    }
    isFirst = false;
    if (groupName) {
      nodes.push(
        <div key={`hdr-${groupName}`} className="multi-select-group-header">
          {groupName}
        </div>
      );
    }
    for (const opt of groupOptions) {
      nodes.push(
        <label key={opt.value} className="multi-select-option">
          <input
            type="checkbox"
            checked={value.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          />
          <span>{opt.label}</span>
        </label>
      );
    }
  }
  return nodes;
}
